/**
 * Recent-position tail for an active aircraft (#804).
 *
 * A marker shows where an aircraft is and, through its rotation, which way it
 * points — but not where it has just been. This builds the short trail drawn
 * behind it, through its recent positions and terminating at the marker itself
 * so the line always meets the icon it belongs to.
 *
 * Only aircraft classified as active get one. That classification belongs to
 * aircraftStatus (#807) and is deferred to rather than repeated here.
 */

import { aircraftStatus } from './affAircraftStatus';
import { logger } from '../../../utils/logger';
import { liftedLineLayout, hasCompleteHeights, evenHeights } from './affLineElevation';

const log = logger('affAircraftTail');

/** Positions behind the marker, as the issue specifies. */
export const TAIL_MAX_POINTS = 4;

type Coord = [number, number];

/**
 * A position that may also carry its altitude, in metres above sea level, as a
 * third element (#820 step 8). Only the first two are ever compared or drawn as
 * geometry; the third rides along untouched.
 */
type PositionWithAltitude = [number, number, ...unknown[]];

const samePoint = (a: PositionWithAltitude | undefined, b: PositionWithAltitude): boolean =>
  !!a && a[0] === b[0] && a[1] === b[1];

/**
 * Whole positions are passed through — sliced and spread, never rebuilt — so a
 * position handed in with its altitude comes back out with it.
 *
 * @param props    live-layer feature properties for the aircraft
 * @param marker   the aircraft's current position
 * @param history  recent unique positions, oldest first
 * @returns the tail's positions, or null when no tail should be drawn
 */
export function buildAircraftTail<P extends PositionWithAltitude = Coord>(
  props: Record<string, any> | null | undefined,
  marker: P,
  history: P[],
): P[] | null {
  if (aircraftStatus(props) !== 'flying') return null;

  const recent = history.slice(-TAIL_MAX_POINTS);

  // The marker terminates the line. Where history already ends there, adding it
  // again would put a zero-length segment on the end of the tail.
  const coords = samePoint(recent[recent.length - 1], marker)
    ? [...recent]
    : [...recent, marker];

  // One point is a marker, not a tail — there is nothing to draw between.
  return coords.length >= 2 ? coords : null;
}

/** Source and layer ids for the tails drawn behind active aircraft. */
export const AFF_TAIL_SOURCE_ID = 'aff-aircraft-tails';
/** Tails drawn on the ground: every tail in 2D, and in 3D those with no heights. */
export const AFF_TAIL_LAYER_ID = 'aff-aircraft-tails-line';
/** Tails lifted to their aircraft's altitude — only ever populated in 3D (#820 step 8). */
export const AFF_TAIL_LIFTED_LAYER_ID = 'aff-aircraft-tails-lifted';

/** #804's look: thinner than a drawn track, so the two never read alike. */
const TAIL_PAINT = { 'line-color': '#1f6feb', 'line-width': 2, 'line-opacity': 0.85 } as const;
const TAIL_LINE_LAYOUT = { 'line-join': 'round', 'line-cap': 'round' } as const;

/**
 * The layers the tails are drawn with, for the map's current mode.
 *
 * Two layers, because a tail with no heights must stay on the ground and under
 * a 'sea' reference an offset of 0 is sea level — underground. So tails with
 * heights and tails without cannot share one elevated layer.
 *
 * In 2D neither is elevated, and the elevation properties are left out entirely
 * rather than set to 'none', so a flat tail never depends on how 'none' treats
 * an offset.
 *
 * The height maths: 'sea' with line-elevation-ground-scale 1 and the aircraft's
 * REAL altitude met the 3D model exactly in the browser (2026-09-13). mapbox
 * applies the terrain exaggeration, so the data never needs to know it, and no
 * terrain is looked up per point — lifted tails cannot hit the off-screen
 * terrain problem the models had (#820 defect a). ground-scale is set
 * explicitly because its default is undocumented.
 */
export function tailLayerSpecs({ is3D }: { is3D: boolean }): any[] {
  // The height maths is shared with drawn tracks (affLineElevation), so a tail
  // and the track it trails into can never be lifted differently.
  const liftedLayout = is3D ? liftedLineLayout(TAIL_LINE_LAYOUT) : { ...TAIL_LINE_LAYOUT };

  return [
    {
      id: AFF_TAIL_LAYER_ID,
      type: 'line',
      source: AFF_TAIL_SOURCE_ID,
      filter: ['!', ['has', 'elevation']],
      layout: { ...TAIL_LINE_LAYOUT },
      paint: { ...TAIL_PAINT },
    },
    {
      id: AFF_TAIL_LIFTED_LAYER_ID,
      type: 'line',
      source: AFF_TAIL_SOURCE_ID,
      filter: ['has', 'elevation'],
      layout: liftedLayout,
      paint: { ...TAIL_PAINT },
    },
  ];
}

/**
 * Build every active aircraft's tail as one FeatureCollection.
 *
 * One source for all of them rather than a source and layer per aircraft: the
 * set changes on every refresh, and adding and removing dozens of mapbox layers
 * each cycle is both slower and far easier to leak.
 */
export function buildTailCollection(
  features: Array<GeoJSON.Feature | any>,
): GeoJSON.FeatureCollection {
  const lines: GeoJSON.Feature[] = [];

  for (const feature of features ?? []) {
    const props = feature?.properties;
    const marker = feature?.geometry?.coordinates as Coord | undefined;
    if (!props || !marker) continue;

    // #820 step 8 — each history position arrives as [lon, lat, alt]. The
    // aircraft's own position is given its altitude too, so the tail can end at
    // the height of the aircraft it trails.
    const history = Array.isArray(props.tail) ? (props.tail as PositionWithAltitude[]) : [];
    const markerWithAltitude: PositionWithAltitude = [marker[0], marker[1], props.altitude];

    const positions = buildAircraftTail(props, markerWithAltitude, history);
    if (!positions) continue;

    // Coordinates and heights are split from the SAME positions, so no trim or
    // dedupe can leave one out of step with the other.
    const coordinates = positions.map(([lon, lat]) => [lon, lat] as Coord);
    const heights = positions.map((p) => p[2]);

    const properties: Record<string, unknown> = { esn: props.esn };
    if (hasCompleteHeights(heights)) {
      // Even in distance, as line-progress reads them (#820).
      properties.elevation = evenHeights(coordinates, heights);
    } else {
      // Decided 2026-09-11 for models, and the same here: no altitude means drawn
      // on the ground, and the gap made visible. Lifting the rest would draw a
      // height that is part guess; dropping the point would hide the gap.
      log.warn(
        `AFF aircraft ${props.name ?? props.esn} has a tail position with no altitude; ` +
          'its tail is drawn on the ground in 3D',
      );
    }

    lines.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties,
    } as GeoJSON.Feature);
  }

  return { type: 'FeatureCollection', features: lines };
}
